import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VoucherFilters as VoucherFiltersType, VoucherStatus, DiscountType } from '@/services/admin/vouchers/vouchers.interfaces';
import { Search, Filter, X } from 'lucide-react';

interface VoucherFiltersProps {
  filters: VoucherFiltersType;
  onFiltersChange: (filters: VoucherFiltersType) => void;
  onReset: () => void;
}

export const VoucherFilters: React.FC<VoucherFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  const [localFilters, setLocalFilters] = useState<VoucherFiltersType>(filters);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof VoucherFiltersType, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleReset = () => {
    setLocalFilters({});
    onReset();
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {Object.keys(filters).length}
              </Badge>
            )}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Ocultar' : 'Avançado'}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        {/* Filtros básicos sempre visíveis */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Código, nome ou descrição..."
                value={localFilters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="w-48">
            <label className="block text-sm font-medium mb-1">Status</label>
            <Select
              value={localFilters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Active">Ativo</SelectItem>
                <SelectItem value="Inactive">Inativo</SelectItem>
                <SelectItem value="Expired">Expirado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-48">
            <label className="block text-sm font-medium mb-1">Tipo de Desconto</label>
            <Select
              value={localFilters.discountType || 'all'}
              onValueChange={(value) => handleFilterChange('discountType', value === 'all' ? undefined : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="percentage">Percentual</SelectItem>
                <SelectItem value="fixedAmount">Valor Fixo</SelectItem>
                <SelectItem value="freeShipping">Frete Grátis</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleApplyFilters} className="mb-0">
            Aplicar
          </Button>
        </div>
      </CardHeader>

      {/* Filtros avançados */}
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Datas de criação */}
            <div>
              <label className="block text-sm font-medium mb-1">Criado após</label>
              <Input
                type="date"
                value={localFilters.createdAfter || ''}
                onChange={(e) => handleFilterChange('createdAfter', e.target.value || undefined)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Criado antes</label>
              <Input
                type="date"
                value={localFilters.createdBefore || ''}
                onChange={(e) => handleFilterChange('createdBefore', e.target.value || undefined)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Válido após</label>
              <Input
                type="date"
                value={localFilters.validAfter || ''}
                onChange={(e) => handleFilterChange('validAfter', e.target.value || undefined)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Válido antes</label>
              <Input
                type="date"
                value={localFilters.validBefore || ''}
                onChange={(e) => handleFilterChange('validBefore', e.target.value || undefined)}
              />
            </div>

            {/* Faixa de utilização */}
            <div>
              <label className="block text-sm font-medium mb-1">Uso mínimo</label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={localFilters.usageRange?.min || ''}
                onChange={(e) => {
                  const min = e.target.value ? parseInt(e.target.value) : undefined;
                  const max = localFilters.usageRange?.max;
                  handleFilterChange('usageRange', min !== undefined || max !== undefined ? { min, max } : undefined);
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Uso máximo</label>
              <Input
                type="number"
                min="0"
                placeholder="∞"
                value={localFilters.usageRange?.max || ''}
                onChange={(e) => {
                  const max = e.target.value ? parseInt(e.target.value) : undefined;
                  const min = localFilters.usageRange?.min;
                  handleFilterChange('usageRange', min !== undefined || max !== undefined ? { min, max } : undefined);
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Criado por</label>
              <Input
                placeholder="Email do criador"
                value={localFilters.createdBy || ''}
                onChange={(e) => handleFilterChange('createdBy', e.target.value || undefined)}
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Componente Badge simples para contagem de filtros
const Badge: React.FC<{ variant?: 'secondary'; children: React.ReactNode; className?: string }> = ({ 
  variant = 'secondary', 
  children, 
  className = '' 
}) => {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
  const variantClasses = variant === 'secondary' ? 'bg-gray-100 text-gray-800' : '';
  
  return (
    <span className={`${baseClasses} ${variantClasses} ${className}`}>
      {children}
    </span>
  );
};
